'use client'

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'

/**
 * Draws a diagram as the page scrolls: sets `--p` on the element, from 0
 * when its top reaches 85% of the viewport to 1 once it has scrolled
 * through (its height, or half a viewport for a short one), so its CSS draws
 * the line and lights each node as the line reaches it. Scrolling back
 * undraws it. The server renders the finished diagram (`--p` is 1 by
 * default), and under reduced motion nothing is set, so it stays finished.
 */
export function ScrollProgress({
  className,
  style,
  children,
}: {
  className?: string
  style?: CSSProperties
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const element = ref.current
    if (!element || typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    let frame = 0
    const update = () => {
      frame = 0
      const box = element.getBoundingClientRect()
      const view = window.innerHeight
      const progress = (view * 0.85 - box.top) / Math.max(box.height, view * 0.45)
      element.style.setProperty('--p', Math.min(1, Math.max(0, progress)).toFixed(3))
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }
    const listen = (on: boolean) => {
      const method = on ? 'addEventListener' : 'removeEventListener'
      window[method]('scroll', schedule, { passive: true })
      window[method]('resize', schedule)
    }
    const observer = new IntersectionObserver(([entry]) => {
      listen(entry.isIntersecting)
      schedule()
    })
    update()
    observer.observe(element)
    return () => {
      observer.disconnect()
      listen(false)
      cancelAnimationFrame(frame)
    }
  }, [])
  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  )
}
